import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { getAssetIcon } from 'helpers/icons';
import { COMPONENT_CONSTANTS } from '../uiConstants';
import SubTypeSelector from '../SubTypeSelector';
import './SelectedAssetItem.scss';

/**
 * SelectedAssetItem - Optimized component for rendering individual selected assets
 * Includes subtype selection if available
 */
const SelectedAssetItem = memo(({
    asset,
    subTypeOptions = [],
    selectedSubType,
    onSubTypeChange,
    onSubTypeClear,
    onRemove,
    style = {}
}) => {
    const handleRemove = () => {
        onRemove(asset);
    };

    const handleSubTypeClear = (asset) => {
        if (onSubTypeClear) {
            onSubTypeClear(asset);
        } else {
            // Default behavior - clear the subtype
            onSubTypeChange(asset, null);
        }
    };

    const hasSubTypeOptions = subTypeOptions && subTypeOptions.length > 0;

    return (
        <div style={style} className='selected-asset-item-wrapper'>
            <div className='selected-asset-item'>
                <div className='asset-icon-container'>
                    {getAssetIcon(asset.assetType, { ...COMPONENT_CONSTANTS.ASSET_ITEM_ICON_SIZE, color: '#666666' })}
                </div>
                <div className='asset-info'>
                    <div className='asset-name' title={asset.assetName}>
                        {asset.assetName}
                    </div>
                    <div className='asset-id'>
                        ({asset.assetId})
                    </div>
                </div>
                <div className='subtype-section'>
                    {hasSubTypeOptions ?
                        <SubTypeSelector
                            asset={asset}
                            selectedSubType={selectedSubType}
                            subTypeOptions={subTypeOptions}
                            onSubTypeChange={onSubTypeChange}
                            onClear={handleSubTypeClear}
                        />
                        :
                        <div className='no-subtype'>
                            <span className='no-subtype-text'>—</span>
                        </div>
                    }
                </div>
                <div className='asset-actions'>
                    <button
                        type='button'
                        className='remove-btn'
                        onClick={handleRemove}
                        title='Remove asset'
                        aria-label={`Remove ${asset.assetName}`}
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
});

SelectedAssetItem.displayName = 'SelectedAssetItem';

SelectedAssetItem.propTypes = {
    asset: PropTypes.shape({
        assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        assetName: PropTypes.string.isRequired,
        assetType: PropTypes.string.isRequired,
        selectedSubType: PropTypes.string, // Subtype is now stored directly on the asset
        outputSpeedRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Output speed ratio for gearbox assets
        speedRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) // Speed ratio from API
    }).isRequired,
    subTypeOptions: PropTypes.array,
    selectedSubType: PropTypes.string,
    onSubTypeChange: PropTypes.func.isRequired,
    onSubTypeClear: PropTypes.func,
    onRemove: PropTypes.func.isRequired,
    style: PropTypes.object
};

export default SelectedAssetItem;
